import { useTranslation } from 'react-i18next';
import { useId, useState } from 'react';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import styles from './DocumentDecisionButtons.module.css';
import { Button } from '../../components/ui/Button';

export function DocumentDecisionButtons({
  documentId,
  pending,
  reason,
  onReasonChange,
  error,
  onDecision,
}: {
  documentId: number;
  pending: boolean;
  reason: string;
  onReasonChange: (reason: string) => void;
  error: string | null;
  onDecision: (approve: boolean) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const helperId = useId();
  return (
    <div className={styles.wrapper}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${helperId}-reason`}>
          {t('shipments.documentDecisionButtons.reasonLabel')}
        </label>
        <textarea
          id={`${helperId}-reason`}
          className={styles.textarea}
          aria-label={t('shipments.documentDecisionButtons.reasonAriaLabel')}
          aria-describedby={helperId}
          rows={3}
          value={reason}
          disabled={pending}
          onChange={(event) => onReasonChange(event.target.value)}
        />
        <p id={helperId} className={styles.helper}>
          {t('shipments.documentDecisionButtons.reasonHelper')}
        </p>
      </div>
      {error && (
        <p role="alert" className={styles.errorText}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <Button variant="primary" size="sm" disabled={pending} onClick={() => setConfirmOpen(true)}>
          {t('shipments.documentDecisionButtons.approve')}
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={pending || reason.trim().length < 3}
          onClick={() => setRejectOpen(true)}
        >
          {t('shipments.documentDecisionButtons.reject')}
        </Button>
      </div>
      <ConfirmDialog
        help="shipment-documents"
        isOpen={confirmOpen}
        variant="success"
        title={t('shipments.documentDecisionButtons.approve')}
        description={`Approve document #${documentId}? It will be marked as verified.`}
        confirmText="Approve"
        loading={pending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void onDecision(true);
        }}
      />
      <ConfirmDialog
        help="shipment-documents"
        isOpen={rejectOpen}
        variant="danger"
        title={t('shipments.documentDecisionButtons.reject')}
        description={t('shipments.documentDecisionButtons.rejectConfirm', {
          id: documentId,
          reason: reason.trim(),
        })}
        confirmText={t('shipments.documentDecisionButtons.rejectConfirmButton')}
        loading={pending}
        onCancel={() => setRejectOpen(false)}
        onConfirm={() => {
          setRejectOpen(false);
          void onDecision(false);
        }}
      />
    </div>
  );
}
